import { EXTERNAL_CS } from '@/consts/systems.consts';
import { Observation } from '@medplum/fhirtypes';
import { calculateAgeAtGivenTime } from '@utils/calculate-age-at-given-time';
import { convertWeightToKg } from '@utils/weight-utils';

// -----------------------------------------------------------------------------
/**
 * Checks if an observation contains any of the specified codes
 * @param observation - The observation to check
 * @param codes - Array of codes to look for
 * @returns True if any of the codes are found in the observation
 */
const hasAnyCode = (observation: Observation, codes: string[]): boolean => {
  const observationCodes =
    observation.code?.coding?.map((coding) => coding.code) || [];
  return codes.some((code) => observationCodes.includes(code));
};

// -----------------------------------------------------------------------------
interface ObservationData {
  date: string;
  value: number;
  age: { years: number; months: number; days: number };
}
// -----------------------------------------------------------------------------
interface GroupedData {
  height: { observations: ObservationData[] };
  weight: { observations: ObservationData[] };
  bmi: { observations: ObservationData[] };
  head_circumference: { observations: ObservationData[] };
  gestationalAge: { observations: ObservationData[] };
}
// -----------------------------------------------------------------------------
export const mapObservationsToGrowthCharts = (
  observations: Observation[],
  patientBirthDate: string
): GroupedData => {
  const groupedData: GroupedData = {
    height: { observations: [] },
    weight: { observations: [] },
    bmi: { observations: [] },
    head_circumference: { observations: [] },
    gestationalAge: { observations: [] },
  };

  observations.forEach((observation) => {
    const date = observation.effectiveDateTime?.slice(0, 10);

    // Check for gestational age
    if (
      hasAnyCode(observation, [EXTERNAL_CS.SNOMED.CODES.GESTATIONAL_AGE]) &&
      observation.valueString
    ) {
      groupedData.gestationalAge.observations.push({
        date: date ?? '',
        value: parseGestationalAge(observation.valueString) ?? 0,
        age: { years: 0, months: 0, days: 0 },
      });
      return;
    }

    const value = observation.valueQuantity?.value;
    const unit = observation.valueQuantity?.unit;

    // Check for birth weight (can have multiple codes like 29463-7 and 364589006)
    if (
      hasAnyCode(observation, [EXTERNAL_CS.SNOMED.CODES.BIRTH_WEIGHT]) &&
      value
    ) {
      const convertedValue = convertWeightToKg(value, unit);
      groupedData.weight.observations.push({
        date: patientBirthDate,
        value: convertedValue,
        age: { years: 0, months: 0, days: 0 },
      });
      return;
    }

    // Check for birth length
    if (
      hasAnyCode(observation, [EXTERNAL_CS.SNOMED.CODES.BIRTH_LENGTH]) &&
      value
    ) {
      groupedData.height.observations.push({
        date: patientBirthDate,
        value,
        age: { years: 0, months: 0, days: 0 },
      });
      return;
    }

    // Check for birth head circumference
    if (
      hasAnyCode(observation, [
        EXTERNAL_CS.SNOMED.CODES.BIRTH_HEAD_CIRCUMFERENCE,
      ]) &&
      value
    ) {
      groupedData.head_circumference.observations.push({
        date: patientBirthDate,
        value,
        age: { years: 0, months: 0, days: 0 },
      });
      return;
    }

    if (typeof value !== 'number' || !date) {
      return;
    }

    const obsData: ObservationData = {
      date,
      value,
      age: calculateAgeAtGivenTime(new Date(patientBirthDate), new Date(date)),
    };

    if (hasAnyCode(observation, [EXTERNAL_CS.LOINC.CODES.BODY_HEIGHT])) {
      groupedData.height.observations.push(obsData);
    }
    if (hasAnyCode(observation, [EXTERNAL_CS.LOINC.CODES.BODY_WEIGHT])) {
      const convertedValue = convertWeightToKg(value, unit);
      groupedData.weight.observations.push({
        ...obsData,
        value: convertedValue,
      });
    }
    if (hasAnyCode(observation, [EXTERNAL_CS.LOINC.CODES.BMI])) {
      groupedData.bmi.observations.push(obsData);
    }
    if (hasAnyCode(observation, [EXTERNAL_CS.LOINC.CODES.HEAD_CIRCUMFERENCE])) {
      groupedData.head_circumference.observations.push(obsData);
    }
  });

  return groupedData;
};
// -----------------------------------------------------------------------------
type AgeObject = { years: number; months: number; days: number };

export const ageObjectToUnit = (
  age: AgeObject,
  unit: 'year' | 'month' | 'week'
): number => {
  if (unit === 'year') {
    return age.years + age.months / 12 + age.days / 365;
  }
  if (unit === 'month') {
    return age.years * 12 + age.months + age.days / 30;
  }
  if (unit === 'week') {
    return age.years * 52 + age.months * 4.345 + age.days / 7;
  }
  return 0;
};
// -----------------------------------------------------------------------------
export const pairWeightAndHeight = (
  weights: { date: string; value: number }[],
  heights: { date: string; value: number }[]
): { length: number; value: number; date: string }[] => {
  const result: { length: number; value: number; date: string }[] = [];

  for (const weight of weights) {
    let closest: { date: string; value: number } | null = null;
    let minDiff = Infinity;

    for (const height of heights) {
      const diff = Math.abs(
        new Date(weight.date).getTime() - new Date(height.date).getTime()
      );
      if (diff < minDiff) {
        minDiff = diff;
        closest = height;
      }
    }
    // Only pair if within 1 day (86400000 ms)
    const ONE_DAY = 86400000;
    if (closest && minDiff < ONE_DAY) {
      result.push({
        length: closest.value,
        value: weight.value,
        date: weight.date,
      });
    }
  }
  return result;
};
// -----------------------------------------------------------------------------
export const parseGestationalAge = (age: string): number | undefined => {
  if (!age) {
    return undefined;
  }
  const match = age.match(/^(\d+)\+(\d+)$/);
  if (!match) {
    return undefined;
  }
  const weeks = parseInt(match[1], 10);
  const days = parseInt(match[2], 10);
  return weeks + days / 7;
};
